import {
  Component,
  ElementRef,
  computed,
  inject,
  input,
  linkedSignal,
  output
} from '@angular/core';
import { Hex } from 'viem';
import { AddressColorPipe } from '../../pipes/address-color.pipe';
import { ProfileCardOverlayService } from '../../services/profile-card-overlay.service';
import { addressEquals } from '../../services/utils.service';
import { OnlineUser } from '../../types/server';
import { FormatSegmentComponent } from '../format-segment/format-segment.component';
import { IdentityComponent } from '../identity/identity.component';
import { PixelCreatureComponent } from '../pixel-creature/pixel-creature.component';

// A directed challenge carries the chosen format so the signed offer's battleMode
// is picked per-player rather than inherited from the global home toggle.
export interface DirectChallengeRequest {
  address: Hex;
  battleMode: number;
}

export function otherOnlinePlayers(
  clients: ReadonlyMap<Lowercase<Hex>, OnlineUser>,
  selfAddress: Hex | undefined,
): { address: Hex; user: OnlineUser }[] {
  return [...clients.entries()]
    .filter(([address]) => !addressEquals(address, selfAddress))
    .map(([address, user]) => ({ address, user }));
}

@Component({
  selector: 'app-online-player-row',
  imports: [PixelCreatureComponent, AddressColorPipe, IdentityComponent, FormatSegmentComponent],
  templateUrl: './online-player-row.component.html',
  styleUrl: './online-player-row.component.css',
  host: {
    '[class.is-expanded]': 'expanded()',
    '[class.is-pending]': 'challengePending()',
    '[class.is-self]': 'isSelf()',
    '(click)': 'onHostClick()',
  },
})
export class OnlinePlayerRowComponent {
  readonly address = input.required<Hex>();
  readonly player = input.required<OnlineUser>();
  readonly isSelf = input(false);
  // The globally-pending direct-challenge address (null if none); the row derives
  // its own pending/disabled state — only one offer is ever in flight.
  readonly pendingAddress = input<Hex | null>(null);
  readonly openProfileOnClick = input(false);
  // Home format (0 singles / 1 doubles) this row seeds from; re-seeds when it changes.
  readonly defaultMode = input(0);

  readonly expanded = input(false);

  readonly challenge = output<DirectChallengeRequest>();
  readonly toggle = output<Hex>();

  readonly mode = linkedSignal(() => this.defaultMode());

  readonly challengePending = computed(() => {
    const pending = this.pendingAddress();
    return !!pending && addressEquals(pending, this.address());
  });
  readonly challengeDisabled = computed(() => {
    const pending = this.pendingAddress();
    return !!pending && !addressEquals(pending, this.address());
  });

  private readonly overlay = inject(ProfileCardOverlayService);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  onHostClick(): void {
    if (this.isSelf() || !this.openProfileOnClick()) {
      this.toggle.emit(this.address());
      return;
    }
    this.overlay.openFor({
      address: this.address(),
      username: this.player().username,
      twitterHandle: this.player().twitterHandle ?? null,
      anchor: this.elementRef.nativeElement,
      placement: 'left',
    });
  }

  onSendClick(event: MouseEvent): void {
    event.stopPropagation();
    this.challenge.emit({ address: this.address(), battleMode: this.mode() });
  }
}
